import React, { useState, useRef, useEffect } from 'react'
import { Box, IconButton, Typography, useTheme, alpha } from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { useLanguage } from '../../contexts/LanguageContext'

interface ImageComparisonSliderProps {
  beforeImage: string
  afterImage: string
  beforeLabel?: string
  afterLabel?: string
  onDownload?: () => void
}

const ImageComparisonSlider: React.FC<ImageComparisonSliderProps> = ({
  beforeImage,
  afterImage,
  beforeLabel = 'Before',
  afterLabel = 'After',
  onDownload
}) => {
  const { t } = useLanguage()
  const theme = useTheme()
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100))
    setSliderPosition(percentage)
  }

  const handleMouseDown = () => setIsDragging(true)
  const handleMouseUp = () => setIsDragging(false)

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) handleMove(e.clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length > 0) {
      handleMove(e.touches[0].clientX)
    }
  }

  // Global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false)
    window.addEventListener('mouseup', handleGlobalMouseUp)
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp)
  }, [])

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '2px solid',
        borderColor: 'success.main',
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.success.main, 0.05),
        position: 'relative'
      }}
    >
      {/* Header with download button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight={600} color="success.main">
          {t('easyWay.advanced.result')}:
        </Typography>
        {onDownload && (
          <IconButton
            size="small"
            onClick={onDownload}
            sx={{ color: 'success.main' }}
          >
            <DownloadIcon />
          </IconButton>
        )}
      </Box>

      {/* Image comparison container */}
      <Box
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        sx={{
          position: 'relative',
          width: '100%',
          maxHeight: 400,
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          borderRadius: 1,
          backgroundColor: 'background.default'
        }}
      >
        {/* After Image (full) */}
        <Box
          component="img"
          src={afterImage}
          alt={afterLabel}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            maxHeight: 400,
            objectFit: 'contain'
          }}
        />

        {/* Before Image (clipped) */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
          }}
        >
          <Box
            component="img"
            src={beforeImage}
            alt={beforeLabel}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              maxHeight: 400
            }}
          />
        </Box>

        {/* Slider Line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `${sliderPosition}%`,
            width: 3,
            height: '100%',
            backgroundColor: 'white',
            boxShadow: '0 0 10px rgba(0,0,0,0.5)',
            transform: 'translateX(-50%)',
            pointerEvents: 'none'
          }}
        />

        {/* Slider Handle */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${sliderPosition}%`,
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'white',
            border: '3px solid',
            borderColor: theme.palette.primary.main,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              width: 0,
              height: 0,
              borderStyle: 'solid'
            },
            '&::before': {
              left: '30%',
              borderWidth: '6px 6px 6px 0',
              borderColor: `transparent ${theme.palette.primary.main} transparent transparent`,
              transform: 'translateX(-50%)'
            },
            '&::after': {
              right: '30%',
              borderWidth: '6px 0 6px 6px',
              borderColor: `transparent transparent transparent ${theme.palette.primary.main}`,
              transform: 'translateX(50%)'
            }
          }}
        />

        {/* Labels */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 600
          }}
        >
          {beforeLabel}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 600
          }}
        >
          {afterLabel}
        </Box>
      </Box>

      {/* Instructions */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
      >
        {t('easyWay.advanced.comparison.instruction')}
      </Typography>
    </Box>
  )
}

export default ImageComparisonSlider
